import { Button, Callout, theme } from '@agentisai/design-system';
import { useSearchParams } from 'react-router';
import { describeError } from '../lib/describeError';
import { useCreateBuilder, useDrafts } from '../queries/useBuilder';
import { AgentLibrary } from './AgentLibrary';
import { WorkstreamCard } from './WorkstreamCard';
import { FORK_PARAM, selectWorkstreamParams, WS_PARAM } from './WorkstreamsRailBody';

const mutedMessage = {
  margin: 0,
  color: theme.color.neutral.faint,
  fontSize: theme.typography.fontSize.bodySm,
  lineHeight: theme.typography.lineHeight.body,
} as const;

/**
 * Agents rail panel body (ADR-0080, AB-2-2) — the caller's own drafts plus
 * **New agent**. Both open the agent's builder workstream (`?ws=`), where Forge
 * answers every turn and the centre pane shows the draft. Below them, the org
 * library (AB-2-5): share, retire, review, and **Fork** — a new builder whose
 * first message starts from the shared agent (`?fork=`).
 */
export function AgentsRailBody() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = searchParams.get(WS_PARAM);
  const drafts = useDrafts();
  const create = useCreateBuilder();

  function open(workstreamId: string) {
    // `?panel=agents` survives, so the rail stays on this list.
    setSearchParams((prev) => selectWorkstreamParams(prev, workstreamId), { replace: true });
  }

  function fork(agentId: string) {
    create.mutate(undefined, {
      onSuccess: (r) =>
        setSearchParams(
          (prev) => {
            const next = selectWorkstreamParams(prev, r.data.workstreamId);
            next.set(FORK_PARAM, agentId);
            return next;
          },
          { replace: true },
        ),
    });
  }

  let list;
  if (drafts.isLoading) {
    list = <p style={mutedMessage}>Loading drafts…</p>;
  } else if (drafts.isError) {
    list = (
      <p role="alert" style={mutedMessage}>
        Couldn't load your drafts. We'll retry automatically.
      </p>
    );
  } else if ((drafts.data ?? []).length === 0) {
    list = (
      <p style={mutedMessage}>
        No drafts yet. Start a new agent and Forge will interview you, then save a draft here.
      </p>
    );
  } else {
    list = (
      <div>
        {(drafts.data ?? []).map((d) =>
          d.builderWorkstreamId ? (
            <WorkstreamCard
              key={d.agentId}
              name={d.displayName}
              description={`Revision ${d.revision} · ${d.status}`}
              active={activeId === d.builderWorkstreamId}
              onClick={() => open(d.builderWorkstreamId as string)}
            />
          ) : (
            // Its builder workstream was deleted, so there is nowhere to open it.
            <p key={d.agentId} style={{ ...mutedMessage, marginBottom: theme.space[2] }}>
              {d.displayName} — revision {d.revision} (builder deleted)
            </p>
          ),
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.space[3],
        padding: `0 ${theme.space[4]}`,
      }}
    >
      <Button
        size="sm"
        variant="secondary"
        fullWidth
        disabled={create.isPending}
        onClick={() => create.mutate(undefined, { onSuccess: (r) => open(r.data.workstreamId) })}
      >
        {create.isPending ? 'Opening…' : '+ New agent'}
      </Button>
      {create.isError && (
        <div role="alert">
          <Callout tone="danger">
            Couldn't start a new agent: {describeError(create.error).userMessage}
          </Callout>
        </div>
      )}
      {list}
      <AgentLibrary onFork={fork} forking={create.isPending} />
    </div>
  );
}
AgentsRailBody.displayName = 'AgentsRailBody';
